import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { DashboardSidebar } from '../components/layout/DashboardSidebar';
import { apiFetch } from '../lib/apiClient';
import { User, Mail, Award, Clock, Flame, BookOpen, Target, TrendingUp, CalendarDays } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';

export default function ProfilePage() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let isMounted = true;
    const fetchProfile = async () => {
      try {
        const response = await apiFetch('/auth/profile', { method: 'GET' });
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || 'Failed to load profile');
        }

        if (isMounted) {
          setProfile(data.profile || data);
        }
      } catch (err) {
        if (isMounted) setError(err.message);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchProfile();
    return () => { isMounted = false; };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(220,20%,97%)]">
        <DashboardSidebar />
        <main className="lg:ml-60 p-4 pt-24 lg:p-8">
          <div className="mb-8 animate-pulse">
            <div className="h-8 w-48 bg-slate-200/60 rounded mb-2"></div>
            <div className="h-4 w-64 bg-slate-200/60 rounded"></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 animate-pulse">
            {/* Left Column Skeleton */}
            <div className="col-span-12 lg:col-span-4 space-y-6">
              {/* Profile Card Skeleton */}
              <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden flex flex-col items-center">
                <div className="h-24 w-full bg-slate-200/60"></div>
                <div className="w-24 h-24 rounded-full border-4 border-white bg-slate-200/60 -mt-12 mb-4"></div>
                <div className="h-6 w-32 bg-slate-200/60 rounded mb-2"></div>
                <div className="h-4 w-48 bg-slate-200/60 rounded mb-4"></div>
                <div className="h-6 w-24 bg-slate-200/60 rounded-full mb-6"></div>
              </div>

              {/* Questionnaire Skeleton */}
              <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-6 space-y-4">
                <div className="h-6 w-40 bg-slate-200/60 rounded mb-4"></div>
                <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                  <div className="h-4 w-24 bg-slate-200/60 rounded"></div>
                  <div className="h-4 w-16 bg-slate-200/60 rounded"></div>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                  <div className="h-4 w-32 bg-slate-200/60 rounded"></div>
                  <div className="h-4 w-20 bg-slate-200/60 rounded"></div>
                </div>
                <div className="space-y-3 pt-2">
                  <div className="h-4 w-12 bg-slate-200/60 rounded mb-2"></div>
                  {[1, 2, 3].map((i) => (
                     <div key={i} className="flex gap-2">
                       <div className="w-4 h-4 rounded bg-slate-200/60"></div>
                       <div className="h-4 w-3/4 bg-slate-200/60 rounded"></div>
                     </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column Skeleton */}
            <div className="col-span-12 lg:col-span-8 space-y-6">
              {/* Top Stats Skeleton */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="bg-white rounded-xl border border-slate-100 shadow-sm p-4 flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-slate-200/60"></div>
                    <div className="space-y-2">
                      <div className="h-6 w-12 bg-slate-200/60 rounded"></div>
                      <div className="h-3 w-24 bg-slate-200/60 rounded"></div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Skill Assessment Skeleton */}
              <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-6 space-y-6">
                <div className="flex justify-between items-end pb-4 border-b border-slate-100">
                  <div className="space-y-2">
                    <div className="h-6 w-32 bg-slate-200/60 rounded"></div>
                    <div className="h-3 w-48 bg-slate-200/60 rounded"></div>
                  </div>
                </div>
                <div className="h-24 w-full bg-slate-200/40 rounded-xl border border-slate-50"></div>
                <div>
                  <div className="h-5 w-40 bg-slate-200/60 rounded mb-4"></div>
                  <div className="grid grid-cols-2 gap-x-8 gap-y-6">
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                      <div key={i} className="space-y-2">
                        <div className="flex justify-between">
                          <div className="h-4 w-24 bg-slate-200/60 rounded"></div>
                          <div className="h-4 w-8 bg-slate-200/60 rounded"></div>
                        </div>
                        <div className="h-1.5 w-full bg-slate-200/60 rounded-full"></div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Activity History Skeleton */}
              <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-6 space-y-4">
                <div className="h-6 w-32 bg-slate-200/60 rounded mb-4"></div>
                <div className="space-y-4">
                  {[1, 2].map((i) => (
                    <div key={i} className="flex justify-between items-center pb-3 border-b border-slate-100 last:border-0 last:pb-0">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-slate-200/60"></div>
                        <div className="space-y-1">
                          <div className="h-4 w-24 bg-slate-200/60 rounded"></div>
                          <div className="h-3 w-32 bg-slate-200/60 rounded"></div>
                        </div>
                      </div>
                      <div className="h-4 w-20 bg-slate-200/60 rounded"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="min-h-screen bg-[hsl(220,20%,97%)]">
        <DashboardSidebar />
        <main className="lg:ml-60 p-4 pt-24 lg:p-8 flex items-center justify-center">
          <p className="text-red-500">{error || 'Failed to load profile.'}</p>
        </main>
      </div>
    );
  }

  const completedCount = profile?.currentRoadmap?.completedExercises?.length || 0;
  const remainingCount = profile?.currentRoadmap?.availableExercises?.length || 0;

  const skillMetrics = Object.entries(profile?.skillGapReport?.skillMetrics || {});

  return (
    <div className="min-h-screen bg-[hsl(220,20%,97%)]">
      <DashboardSidebar />
      <main className="lg:ml-60 p-4 pt-24 lg:p-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-[hsl(222,47%,11%)] font-['Outfit'] mb-1">
            User Profile
          </h1>
          <p className="text-slate-600 text-sm">
            Manage your artist profile, skills, and learning progress.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="col-span-12 lg:col-span-4 space-y-6">
            <Card className="card-elevated border-0 text-center relative overflow-hidden">
              <div className="h-24 bg-gradient-to-r from-[hsl(234,89%,63%)] to-[hsl(262,83%,58%)] opacity-80" />
              <CardContent className="px-6 pb-6 pt-0 relative z-10">
                <div className="w-24 h-24 mx-auto bg-white rounded-full border-4 border-white shadow-md flex items-center justify-center text-[hsl(222,47%,11%)] text-3xl font-bold -mt-12 bg-gradient-to-br from-slate-100 to-slate-200">
                  {profile.displayName?.[0]?.toUpperCase() || 'A'}
                </div>
                <h2 className="text-xl font-bold font-['Outfit'] mt-4 text-[hsl(222,47%,11%)]">
                  {profile.displayName || 'Artist'}
                </h2>
                <div className="flex items-center justify-center gap-2 mt-1 text-sm text-slate-500">
                  <Mail className="w-4 h-4" />
                  <span>{profile.email}</span>
                </div>
                <div className="mt-4 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[hsl(234,89%,63%)]/10 text-[hsl(234,89%,63%)] text-xs font-semibold">
                  <Award className="w-3.5 h-3.5" />
                  {profile.styleTrack || 'Beginner Track'}
                </div>
              </CardContent>
            </Card>

            <Card className="card-elevated border-0">
              <CardHeader className="pb-3 text-sm">
                <CardTitle className="font-['Outfit'] flex items-center gap-2">
                  <Target className="w-4 h-4 text-[hsl(262,83%,58%)]" />
                  Artist Questionnaire
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center text-sm border-b border-slate-100 pb-2">
                  <span className="text-slate-500">Experience Level</span>
                  <span className="font-medium text-[hsl(222,47%,11%)]">{profile.questionnaire?.experienceLevel || '-'}</span>
                </div>
                <div className="flex justify-between items-center text-sm border-b border-slate-100 pb-2">
                  <span className="text-slate-500">Practice Frequency</span>
                  <span className="font-medium text-[hsl(222,47%,11%)]">{profile.questionnaire?.practiceFrequency || '-'}</span>
                </div>
                {profile.questionnaire?.goals?.length > 0 && (
                  <div className="text-sm">
                    <span className="text-slate-500 block mb-2">Goals</span>
                    <ul className="space-y-1">
                      {profile.questionnaire.goals.map((goal, i) => (
                        <li key={i} className="flex items-start gap-2 text-slate-700">
                          <Target className="w-3.5 h-3.5 mt-0.5 text-slate-400" />
                          <span>{goal}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="col-span-12 lg:col-span-8 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="card-elevated border-0">
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[hsl(12,95%,65%)]/10 flex items-center justify-center">
                    <Flame className="w-6 h-6 text-[hsl(12,95%,65%)]" />
                  </div>
                  <div>
                    <p className="text-xl font-bold text-[hsl(222,47%,11%)]">{profile.streak?.current || 0}</p>
                    <p className="text-xs text-slate-500">Current Streak</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="card-elevated border-0">
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[hsl(234,89%,63%)]/10 flex items-center justify-center">
                    <BookOpen className="w-6 h-6 text-[hsl(234,89%,63%)]" />
                  </div>
                  <div>
                    <p className="text-xl font-bold text-[hsl(222,47%,11%)]">{completedCount}</p>
                    <p className="text-xs text-slate-500">Completed Exercises</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="card-elevated border-0">
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[hsl(142,76%,36%)]/10 flex items-center justify-center">
                    <Target className="w-6 h-6 text-[hsl(142,76%,36%)]" />
                  </div>
                  <div>
                    <p className="text-xl font-bold text-[hsl(222,47%,11%)]">{remainingCount}</p>
                    <p className="text-xs text-slate-500">Remaining Roadmap</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card className="card-elevated border-0">
              <CardHeader className="pb-4 border-b border-slate-100 flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-base font-['Outfit'] flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-[hsl(234,89%,63%)]" />
                    Skill Assessment
                  </CardTitle>
                  <p className="text-xs text-slate-500 mt-1">Based on initial ai analysis</p>
                </div>
              </CardHeader>
              <CardContent className="pt-6 space-y-6">
                <div className="bg-blue-50/50 p-4 rounded-xl border border-blue-100 text-sm text-[hsl(222,47%,11%)] leading-relaxed">
                  {profile.skillGapReport?.aiAnalysisSummary || 'No recent analysis available.'}
                </div>

                {skillMetrics.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-slate-700 mb-4">Core Skills Proficiency</h3>
                    <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                      {skillMetrics.map(([skill, level]) => (
                        <div key={skill} className="space-y-1.5">
                          <div className="flex justify-between items-center text-xs">
                            <span className="font-medium text-slate-600 capitalize">
                              {skill.replace(/([A-Z])/g, ' $1').trim()}
                            </span>
                            <span className="font-semibold text-[hsl(234,89%,63%)]">{level}/10</span>
                          </div>
                          <Progress value={Number(level) * 10} className="h-1.5" />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="card-elevated border-0">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-['Outfit'] flex items-center gap-2">
                  <CalendarDays className="w-4 h-4 text-[hsl(262,83%,58%)]" />
                  Activity History
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 mt-2">
                  <div className="flex justify-between items-center border-b border-slate-100 pb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                        <User className="w-4 h-4 text-slate-500" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-[hsl(222,47%,11%)]">Account Created</p>
                        <p className="text-xs text-slate-500">Joined the community</p>
                      </div>
                    </div>
                    <span className="text-xs font-semibold text-slate-600">
                      {new Date(profile.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center pt-1 pb-2">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                        <Clock className="w-4 h-4 text-slate-500" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-[hsl(222,47%,11%)]">Last Active</p>
                        <p className="text-xs text-slate-500">Most recent login session</p>
                      </div>
                    </div>
                    <span className="text-xs font-semibold text-slate-600">
                      {profile.lastLoginAt ? new Date(profile.lastLoginAt).toLocaleDateString() : 'Unknown'}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
